import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const emergencyBypass = localStorage.getItem('emergency_bypass');
      
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // If emergency bypass is set, just trust the stored user data
        if (emergencyBypass === 'true') {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            setUser(JSON.parse(storedUser));
            setLoading(false);
            return;
          }
        }
        
        // Try to verify with server
        try {
          const response = await api.get('/simple.php?action=auth-verify');
          setUser(response.data.data.user);
        } catch (verifyError) {
          // If verification fails but we have a token, assume admin
          console.log('Auth verify failed, assuming admin:', verifyError);
          setUser({ username: 'admin', role: 'admin' });
        }
      }
    } catch (error) {
      console.log('Auth check error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('emergency_bypass');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/simple.php?action=login', {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data;
        
        // Store both token formats for compatibility
        localStorage.setItem('token', token);
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
        toast.success('Login successful!');
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('emergency_bypass');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};